import { RootState } from "../../../../redux-store";
import { createSelector } from "@reduxjs/toolkit";

const selectEntities = (state: RootState) =>
  state.data.economics.customerDeliveryLocations.entities;

const selectIdsByClientId = (state: RootState) =>
  state.data.economics.customerDeliveryLocations.idsByClientId;

export const selectDeliveryLocationsLoading = (state: RootState) =>
  state.data.economics.customerDeliveryLocations.loading;

export const selectDeliveryLocationsLoadedClientId = (state: RootState) =>
  state.data.economics.customerDeliveryLocations.loadedClientId;

/**
 * All delivery locations for a given client, in load order.
 *
 * Memoized per clientId so repeated calls (e.g. across renders) reuse the same
 * selector instance instead of creating a cold, unmemoized one each time.
 */
const deliveryLocationsForClientCache: Record<
  string,
  ReturnType<typeof makeDeliveryLocationsForClientSelector>
> = {};

const makeDeliveryLocationsForClientSelector = (clientId: string) =>
  createSelector(
    selectEntities,
    selectIdsByClientId,
    (entities, idsByClientId) =>
      (idsByClientId[clientId] ?? [])
        .map((id) => entities[id])
        .filter((location): location is NonNullable<typeof location> =>
          Boolean(location)
        )
  );

export const selectDeliveryLocationsForClient = (clientId: string) => {
  if (!deliveryLocationsForClientCache[clientId]) {
    deliveryLocationsForClientCache[clientId] =
      makeDeliveryLocationsForClientSelector(clientId);
  }
  return deliveryLocationsForClientCache[clientId];
};

/**
 * Delivery locations that have been geocoded (have resolved coordinates),
 * exposed for the location search dropdown. Locations without a resolved place
 * are omitted for now since they have no coordinates to select.
 */
export const selectResolvedDeliveryLocations = createSelector(
  selectEntities,
  (entities) =>
    Object.values(entities).filter((l) => l.resolvedPlace !== null)
);
